/**
 * `RecycleBinLayout` — the Cases Recycle Bin surface
 * (`cases-recycle-bin-lexide-handoff-2026-07-19.md`).
 *
 * Lives in the CASES area rather than under settings. ANY user can OPEN the
 * bin and see what it holds — deliberately, so a lawyer whose case was
 * retired can see it still exists rather than believe it is gone — while
 * every ACTION is org-admin only per the operator ruling of 2026-07-19.
 * Filing the surface itself behind an admin location would hide that
 * reassurance from exactly the person who needs it.
 *
 * NO PURGE IN v1 (operator ruling 2026-07-19). The permanent-destroy action
 * is deliberately absent from every affordance here — an org admin may retire
 * and restore, and nothing in the product destroys case data. The server
 * method still exists and stays server-gated; this surface simply does not
 * call it. Do not re-add a purge control without a new ruling.
 *
 * Rendering rules that are load-bearing, not cosmetic:
 *   - rows key on `folder`, never `caseId` — the bin can hold two
 *     retirements of the same case, and caseId can be null outright;
 *   - an entry whose `CASE_MODEL.json` is unreadable renders an explicit
 *     fault, never a name derived from the folder slug (that derivation
 *     habit hid the case-identity desync for a session);
 *   - "retired by" shows whatever lexserver could resolve — a real name, an
 *     email, or nothing. The label stays neutral rather than claiming which,
 *     and a `runtime:{org}` service principal never reaches it;
 *   - a failed listing shows an error, never an empty bin: those two
 *     states mean opposite things to someone recovering a case.
 */
import { useEffect, useState } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Loader2,
  RotateCcw,
  Trash2,
  Undo2,
} from "lucide-react";

import { useRecycleBin } from "@/hooks/useRecycleBin";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import {
  recycleBinErrorCode,
  restoreFromRecycleBin,
  type RecycleBinEntry,
} from "@/api/lx-recycle-bin";
import { DestructiveCaseDialog } from "@/components/cases/DestructiveCaseDialog";

export function RecycleBinLayout() {
  const { t, i18n } = useTranslation();
  const { entries, total, hasMore, isLoading, isDisconnected, error, refetch, loadMore } =
    useRecycleBin();
  const { identity } = useAuthIdentity();
  const isOrgAdmin = identity?.isOrgAdmin ?? false;

  // The bin is a CASES-AREA destination, not a case — same as `/cases`,
  // which drops to org scope and renders "Área de trabalho" in the side
  // panel. Without this the route kept the previous case in scope while
  // `CaseWorkspaceLayout` unmounted and cleared its data, so the panel
  // showed that case's stale file tree under the phantom title
  // "Caso sem título": two sources disagreeing about whether a case is
  // open. Dropping the scope makes this route behave like its sibling.
  const clearActiveContext = useActiveContextStore((s) => s.clear);
  useEffect(() => {
    clearActiveContext();
  }, [clearActiveContext]);

  const [busyFolder, setBusyFolder] = useState<string | null>(null);
  // NEVER pass the folder as the case NAME. A `{slug}--{uid}--{ISO}` string
  // dropped into "Restaurar «…»?" reads as a name the lawyer should recognise,
  // which is the derivation habit the contract bans and which hid the
  // case-identity desync for a session. An unreadable entry says so, exactly as
  // the purge dialog does.
  const entryLabel = (entry: RecycleBinEntry) => entry.title ?? t("recycleBin.unreadableEntry");

  const [rowError, setRowError] = useState<
    { folder: string; message: string; code?: string } | null
  >(null);

  // ⚖ The restore confirm is the PRODUCT's dialog, not the browser's
  // (ui-tester I-16, run ui-20260903-2241). A native `confirm()` blocks the
  // renderer — the run that hit this one spent a window diagnosing a "wedged"
  // Chrome before finding the prompt underneath — and its buttons are the
  // browser's, so a pt-BR lawyer got a Portuguese question with English
  // OK/Cancel. It is deliberately the LIGHT dialog, not the typed-phrase one
  // the sibling RETIRE action uses: restoring puts something back, and
  // charging destructive-grade friction here would devalue it where it is
  // earned.
  const [pendingRestore, setPendingRestore] = useState<RecycleBinEntry | null>(null);

  const askRestore = (entry: RecycleBinEntry) => setPendingRestore(entry);

  const handleRestore = async (entry: RecycleBinEntry) => {
    setPendingRestore(null);
    setBusyFolder(entry.folder);
    setRowError(null);
    try {
      const res = await restoreFromRecycleBin(entry.folder);
      await refetch();
      // The workspace IS back, but registry-backed lists may still disagree.
      // Reporting plain success here would leave the lawyer staring at a case
      // list that contradicts the action they just took.
      if (res?.registrySynced === false) {
        setRowError({
          folder: entry.folder,
          message: t("recycleBin.registryLagWarning"),
        });
      }
    } catch (err) {
      // Server messages are user-facing pt-BR — surfaced verbatim. NO_RECEIPT
      // in particular must NOT be presented as retryable: the original folder
      // name is genuinely ambiguous, so a retry could only guess.
      const message = err instanceof Error ? err.message : String(err);
      setRowError({
        folder: entry.folder,
        message,
        code: recycleBinErrorCode(err),
      });
    } finally {
      setBusyFolder(null);
    }
  };


  return (
    <div className="flex flex-1 flex-col overflow-auto p-6" data-testid="recycle-bin">
      <div className="mx-auto w-full max-w-4xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-serif-display text-xl font-semibold text-slate-800 dark:text-slate-100">
            {t("recycleBin.title")}
          </h2>
          {!isLoading && !error && (
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {t("recycleBin.count", { count: total })}
            </span>
          )}
        </div>
        <p className="mb-6 text-xs text-slate-500 dark:text-slate-400">
          {t("recycleBin.subtitle")}
        </p>

        {/* Waiting on the socket is NOT a failure — it resolves itself, and
            the listing refreshes automatically when the gateway returns. */}
        {isDisconnected && entries.length === 0 && (
          <div
            data-testid="recycle-bin-reconnecting"
            className="flex items-center justify-center py-20 text-slate-400"
          >
            <Loader2 size={22} className="animate-spin" />
            <span className="ml-2 text-sm">{t("recycleBin.reconnecting")}</span>
          </div>
        )}
        {!isDisconnected && isLoading && entries.length === 0 && (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 size={22} className="animate-spin" />
            <span className="ml-2 text-sm">{t("recycleBin.loading")}</span>
          </div>
        )}

        {/* An unreachable bin must never be mistaken for an empty one. */}
        {error && !isDisconnected && (
          <div
            data-testid="recycle-bin-error"
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            <div className="flex items-center gap-2">
              <AlertCircle size={16} />
              <span>{t("recycleBin.loadFailed", { message: error })}</span>
            </div>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-2 rounded border border-red-300 px-2 py-1 text-[11px] hover:bg-red-100 dark:border-red-800 dark:hover:bg-red-900/40"
            >
              {t("common.retry")}
            </button>
          </div>
        )}

        {!error && !isLoading && !isDisconnected && entries.length === 0 && (
          <div
            data-testid="recycle-bin-empty"
            className="flex flex-col items-center justify-center py-20 text-slate-400 dark:text-slate-500"
          >
            <Trash2 size={44} strokeWidth={1.5} />
            <p className="mt-4 text-sm">{t("recycleBin.empty.title")}</p>
            <p className="mt-1 text-xs">{t("recycleBin.empty.hint")}</p>
          </div>
        )}

        {/* Rows are hidden while the listing is failing: acting on a stale
            snapshot of a RECOVERY surface (restoring an entry the server may
            no longer hold) is worse than showing nothing but the error. */}
        {!error && entries.length > 0 && (
          <ul className="divide-y divide-cream-200/70 rounded-lg border border-cream-200/70 dark:divide-coffee-800/70 dark:border-coffee-800/70">
            {entries.map((entry) => (
              <RecycleBinRow
                key={entry.folder}
                entry={entry}
                isOrgAdmin={isOrgAdmin}
                busy={busyFolder === entry.folder}
                error={rowError?.folder === entry.folder ? rowError.message : null}
                errorCode={
                  rowError?.folder === entry.folder ? rowError.code : undefined
                }
                onRestore={() => void handleRestore(entry)}
              />
            ))}
          </ul>
        )}

        {!error && hasMore && (
          <button
            type="button"
            data-testid="recycle-bin-load-more"
            onClick={() => void loadMore()}
            disabled={isLoading}
            className="mt-4 w-full rounded border border-cream-200 py-2 text-xs text-slate-600 hover:bg-cream-50 disabled:opacity-50 dark:border-coffee-800 dark:text-slate-300 dark:hover:bg-coffee-900"
          >
            {isLoading ? t("recycleBin.loading") : t("recycleBin.loadMore")}
          </button>
        )}
      </div>

      <ConfirmDialog
        open={pendingRestore !== null}
        onClose={() => setPendingRestore(null)}
        title={t("recycleBin.restore")}
        description={
          pendingRestore
            ? t("recycleBin.confirmRestore", { name: entryLabel(pendingRestore) })
            : ""
        }
        confirmButtonLabel={t("recycleBin.restore")}
        cancelButtonLabel={t("common.cancel", "Cancelar")}
        onConfirm={() => {
          if (pendingRestore) void handleRestore(pendingRestore);
        }}
        testId="recycle-bin-restore-confirm"
      />
    </div>
  );
}

function RecycleBinRow({
  entry,
  isOrgAdmin,
  busy,
  error,
  errorCode,
  onRestore,
}: {
  entry: RecycleBinEntry;
  isOrgAdmin: boolean;
  busy: boolean;
  error: string | null;
  /** Contract code from the thrown error — NOT parsed from the message,
   *  which is localized prose. */
  errorCode?: string;
  onRestore: () => void;
}) {
  const { t, i18n } = useTranslation();
  // NO_RECEIPT is terminal: the entry cannot be auto-restored and a retry
  // could only guess at its original folder, so the action is withdrawn
  // rather than left to fail again.
  const notRestorable = errorCode === "NO_RECEIPT";

  return (
    <li
      data-testid={`recycle-bin-row-${entry.folder}`}
      className="flex items-start gap-3 px-4 py-3"
    >
      <div className="min-w-0 flex-1">
        {entry.title ? (
          <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
            {entry.title}
          </div>
        ) : (
          <div
            data-testid={`recycle-bin-unreadable-${entry.folder}`}
            className="flex items-center gap-1 text-sm font-medium text-amber-700 dark:text-amber-400"
          >
            <AlertCircle size={13} aria-hidden />
            {t("recycleBin.unreadableEntry")}
          </div>
        )}

        <div className="mt-0.5 break-all font-mono text-[10px] text-slate-400 dark:text-slate-500">
          {entry.folder}
        </div>

        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">
          {/* The bare `entry.caseId` was removed here 2026-07-27 — no raw entity
              id may reach user-facing copy. Nothing is lost: this row already
              leads with `entry.title` (or an explicit "unreadable entry" state)
              and shows `entry.folder` just above, which is the human-readable
              slug path AND the better identifier to quote at support. */}
          <span>
            {entry.retiredAt
              ? t("recycleBin.retiredAt", {
                  when: new Date(entry.retiredAt).toLocaleString(i18n.language),
                })
              : t("recycleBin.retiredAtUnknown")}
          </span>
          {/* Contract v2: this is a real NAME when lexserver has one, an
              email otherwise, and null when neither resolves — so the label
              is neutral ("por X") rather than asserting which one it got. */}
          {entry.retiredBy.displayName && (
            <span>
              {t("recycleBin.retiredBy", { who: entry.retiredBy.displayName })}
            </span>
          )}
        </div>

        {error && (
          <div
            data-testid={`recycle-bin-row-error-${entry.folder}`}
            role="alert"
            className="mt-1.5 rounded border border-red-200/70 bg-red-50 px-2 py-1 text-[11px] text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {/* Restore is ORG-ADMIN ONLY (operator ruling 2026-07-19, shipped
            22cee041 — it was previously "anyone who can delete"). The server
            gate is authoritative and fails closed; hiding the control keeps a
            non-admin from being handed a button that can only refuse. */}
        {isOrgAdmin && !notRestorable && (
          <button
            type="button"
            data-testid={`recycle-bin-restore-${entry.folder}`}
            onClick={onRestore}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-cream-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-cream-100 disabled:opacity-50 dark:border-coffee-700 dark:text-slate-200 dark:hover:bg-coffee-800"
          >
            {busy ? (
              <Loader2 size={11} className="animate-spin" aria-hidden />
            ) : (
              <Undo2 size={11} aria-hidden />
            )}
            {t("recycleBin.restore")}
          </button>
        )}
      </div>
    </li>
  );
}
